import { useState, useCallback, useMemo } from 'react';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { catalogApiRef, entityRouteRef, useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';
import { getCompoundEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { AdHocJobWorkflow } from './AdHocJobWorkflow';
import { SecretsContextProvider } from '@backstage/plugin-scaffolder-react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Theme,
} from '@material-ui/core';
import { useNavigate } from 'react-router';

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(2, 2),
  },
  workflowContainer: {
    marginTop: '24px',
  },
  resetButton: {
    marginLeft: '5px',
  },
}));

export const EntityAdHocJobContent = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const { entity } = useEntity();
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);

  const [templateName, setTemplateName] = useState<string>('');

  const { loading, value: catalogResponse } = useAsync(() => {
    return catalogApi.getEntities({
      fields: ['metadata.name', 'metadata.description'],
      filter: {
        kind: 'template',
        'spec.type': 'job',
        'metadata.annotations.realtor.com/adhoc-job-entity': stringifyEntityRef(entity),
      },
    });
  }, [catalogApi]);

  const templates = useMemo(
    () =>
      catalogResponse?.items?.map(({ metadata }) => ({
        label: metadata.name,
        description: metadata.description,
        value: metadata.name,
      })) || [],
    [catalogResponse],
  );

  const resetHandler = useCallback(() => {
    setTemplateName('');
    navigate(`${entityRoute(getCompoundEntityRef(entity))}/ad-hoc-jobs`);
  }, [navigate, entityRoute, entity]);

  return (
    <SecretsContextProvider>
      <Grid container spacing={3} alignItems="flex-end">
        <Grid item xs={12}>
          <Paper className={classes.bar}>
            <Box component="span" alignItems="flex-end" display="flex" flexDirection="row">
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="select-job-label">Available Jobs</InputLabel>
                <Select
                  value={templateName}
                  label="Select Job"
                  labelId="select-job-label"
                  data-testid="select-job-input"
                  disabled={loading}
                  onChange={event => setTemplateName(event.target.value as string)}
                  renderValue={(value: any) => value}
                >
                  {templates.map((option, idx) => (
                    <MenuItem key={idx} value={option.value as any} data-testid="template-item">
                      <ListItemText primary={option.label} secondary={option.description} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button onClick={resetHandler} className={classes.resetButton}>
                Reset
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      {templateName && <AdHocJobWorkflow templateName={templateName} />}
    </SecretsContextProvider>
  );
};
